import { useForm } from '@tanstack/react-form';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { z, ZodError } from 'zod';

import styles from '../styles.module.css';
import Button from '@/components/LoginComponents/Button';
import Input from '@/components/LoginComponents/Input';
import LinkButton from '@/components/LoginComponents/Link';
import { POOL_NAME } from '@/constants';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { startPresignupRecoveryLogout } from '@/oidcConfig';
import {
  changeUserPassword,
  clearStatus,
  signInAndFill,
  signOidcAndFill,
  signOut
} from '@/slices/authSlice';
import { RootState } from '@/store';
import {
  clearOidcPresignupRecoveryFlags,
  clearOidcPresignupSsoRetryPending,
  getOidcPresignupErrorMessage,
  hasOidcPresignupLogoutAttempted,
  isOidcPresignupError,
  isOidcPresignupSsoRetryPending,
  setOidcPresignupLogoutAttempted,
  setOidcPresignupSsoRetryPending
} from '@/solaris-lens-common-lib-web/src/utils/oidcLogin';

const loginSchema = z.object({
  username: z.string().min(1, 'Username should not be empty'),
  password: z.string().min(1, 'Password should not be empty')
});

function LoginContainer() {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isPresignupRecoveryLoading, setIsPresignupRecoveryLoading] =
    useState(false);
  const [oidcPresignupError, setOidcPresignupError] = useState('');
  const authState = useSelector((state: RootState) => state.auth);
  const errorResponse = authState?.error?.description;
  const displayErrorResponse = errorResponse || oidcPresignupError;
  const presignupRecoveryStartedRef = useRef(false);
  const presignupSsoRetryStartedRef = useRef(false);

  const form = useForm({
    defaultValues: {
      username: '',
      password: ''
    },
    validators: {
      onSubmit: loginSchema
    },
    onSubmit: async ({ value }) => {
      setIsLoading(true);
      await dispatch(
        signInAndFill({
          username: value.username,
          password: value.password,
          poolName: POOL_NAME
        })
      );
      setIsLoading(false);
    }
  });

  const auth = useAuth();

  const onOidcLogin = () => {
    setOidcPresignupError('');
    clearOidcPresignupRecoveryFlags();
    auth.signinRedirect();
  };

  const runPresignupRecoveryLogout = useCallback(async () => {
    setIsPresignupRecoveryLoading(true);
    setOidcPresignupLogoutAttempted();
    setOidcPresignupSsoRetryPending();

    const idToken = auth.user?.id_token;
    const email = auth.user?.profile?.email as string | undefined;

    dispatch(signOut());
    localStorage.clear();
    await Promise.allSettled([auth.removeUser(), auth.clearStaleState()]);

    startPresignupRecoveryLogout(idToken, email);
  }, [auth, dispatch]);

  useEffect(() => {
    if (
      auth.isAuthenticated &&
      auth?.user &&
      auth?.error?.message !== 'invalid_grant'
    ) {
      clearOidcPresignupRecoveryFlags();
      setOidcPresignupError('');
      dispatch(
        signOidcAndFill({
          ...auth.user,
          accessToken: auth.user.access_token ?? '',
          refreshToken: auth.user.refresh_token ?? '',
          tokenType: auth.user.token_type ?? '',
          idToken: auth.user.id_token ?? '',
          expiresIn: auth.user.expires_at ?? 0,
          isOidcClient: true,
          poolName: POOL_NAME
        })
      );
    }
  }, [auth.isAuthenticated, auth.error?.message, auth.user, dispatch]);

  useEffect(() => {
    dispatch(clearStatus());
  }, [dispatch]);

  useEffect(() => {
    const search = window.location.search;

    if (!isOidcPresignupError(search)) {
      setOidcPresignupError('');
      return;
    }

    if (hasOidcPresignupLogoutAttempted()) {
      setOidcPresignupError(getOidcPresignupErrorMessage(search));
      return;
    }

    if (presignupRecoveryStartedRef.current) return;
    if (auth.isLoading || auth.activeNavigator) return;

    presignupRecoveryStartedRef.current = true;
    void runPresignupRecoveryLogout();
  }, [auth.activeNavigator, auth.isLoading, runPresignupRecoveryLogout]);

  useEffect(() => {
    if (!isOidcPresignupSsoRetryPending()) return;
    if (presignupSsoRetryStartedRef.current) return;
    if (isOidcPresignupError(window.location.search)) return;
    if (auth.isLoading || auth.activeNavigator) return;

    presignupSsoRetryStartedRef.current = true;
    clearOidcPresignupSsoRetryPending();
    auth.signinRedirect();
  }, [auth, auth.activeNavigator, auth.isLoading]);

  if (authState?.error?.code === 1003) {
    dispatch(
      changeUserPassword({
        ...authState,
        data: {
          username: form.getFieldValue('username') || '',
          password: form.getFieldValue('password') || ''
        }
      })
    );
    navigate('/change-password');
  }

  return (
    <div className={styles.formWrapper}>
      {displayErrorResponse && (
        <div className={styles.formErrors}>{displayErrorResponse}</div>
      )}
      <div className={styles.form}>
        <h3>Login</h3>
        <form.Field name="username">
          {field => (
            <Input
              type="text"
              placeholder="Email / Username"
              value={field.state.value}
              onChange={value => field.handleChange(value || '')}
              errors={field?.state?.meta?.errors as ZodError[]}
            />
          )}
        </form.Field>

        <form.Field name="password">
          {field => (
            <Input
              type="password"
              placeholder="Password"
              value={field.state.value}
              onChange={value => field.handleChange(value || '')}
              errors={
                (field?.state?.meta?.errors as ZodError[]) || [
                  displayErrorResponse
                ]
              }
            />
          )}
        </form.Field>

        <div className={styles.buttonContainer}>
          <Button
            label="SUBMIT"
            isLoading={isLoading}
            onClick={() => form.handleSubmit()}
          />
          <LinkButton label="Forgot Password?" to="/forgot-password" />
        </div>

        <div className={styles.separator}>
          <div className={styles.separatorLine}></div>
          <p className={styles.separatorText}>OR</p>
          <div className={styles.separatorLine}></div>
        </div>

        <Button
          label="LOG IN WITH SSO"
          isLoading={auth.isLoading || isPresignupRecoveryLoading}
          disabled={auth.isLoading || isPresignupRecoveryLoading}
          onClick={() => onOidcLogin()}
        />
      </div>
    </div>
  );
}

export default LoginContainer;
